import {
  ChevronRight as ChevronRightIcon,
  ErrorOutline as ErrorOutlineIcon,
  LockOutlined as LockOutlinedIcon,
  TimerOutlined as TimerOutlinedIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  fetchSlaDefinitions,
  getOrganizationCalendar,
  isSlaFixtureMode,
  previewSlaDefinition,
  processmodelsApi,
  toSlaApiError,
} from "api";
import { getTranslation } from "common";
import DialogHeader from "components/dialogs/DialogHeader";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { useTranslations, useUser } from "hooks";
import dayjs from "dayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SlaPreviewResponse, SlaDurationUnit } from "model/slaModel";
import { SLA_ERROR_CODES, SLA_TARGET_PROCESS } from "model/slaModel";
import {
  SLA_DURATION_UNITS,
  SLA_DURATION_UNIT_LABEL_KEYS,
  SLA_EDITOR_TEXT_KEYS,
  slaRevisionFromMeta,
} from "model/slaEditorModel";
import {
  SLA_START_TEXT_KEYS,
  buildSlaStartPayload,
  createSlaPreviewSequencer,
  emptySlaStartDraft,
  isSlaStartDraftConfigured,
  slaStartAbsoluteDeadlineAllowed,
  slaStartDraftFromDefinition,
  slaStartDraftIsOverridden,
  slaStartPreviewRequest,
  slaNodeNamesFromBpmnXml,
  slaStartReadsAbsoluteFormat,
  slaStartTargetKey,
  validateSlaStartOverride,
  type SlaStartDraft,
  type SlaStartPayload,
} from "model/slaStartModel";
import {
  applySlaSuggestion,
  isSlaSuggestionApplied,
  slaSuggestionApplySupported,
  slaSuggestionFromPreview,
  type SlaSuggestion,
} from "model/slaSuggestionModel";
import SlaSuggestions from "./SlaSuggestions";

/**
 * Array stabili: `defaults ?? []` e `drafts` entrano nelle dipendenze degli
 * effetti che pubblicano il frammento, quindi un letterale nuovo a ogni render
 * provocherebbe un ciclo di aggiornamenti.
 */
const NO_DRAFTS: SlaStartDraft[] = [];

/** Chiave del target di processo: `target_designerid` e' NULL nel contratto. */
const PROCESS_TARGET_KEY = slaStartTargetKey({
  targetType: SLA_TARGET_PROCESS,
  targetDesignerId: null,
});

/**
 * Hidden for now: solo il blocco "Suggerimenti" (proposta di scadenza con
 * "Applica suggerimento" / "Suggerimento applicato") del modale di avvio.
 * Il blocco "Anteprima" (selettore target, Aggiorna, scadenza / rischio /
 * as_of stimati) resta visibile: mostra quando scadrebbe lo SLA con i valori
 * modificati. Il codice delle proposte resta: rimettere `true` per
 * ripristinarle.
 */
const SLA_START_AI_SUGGESTIONS_VISIBLE = false;

/**
 * Superficie **Avvio processo SLA** (piano Client sez. 3 riga "Avvio", sez. 5
 * F2b e sez. 8 riga 124).
 *
 * Innesto: **sezione del dialog esistente** `components/dialogs/StartProcess.tsx`
 * (step 2, "Dettagli"). Non una rotta nuova: l'avvio di processo esiste gia' li'
 * e il piano prescrive l'innesto, non una superficie parallela.
 *
 * Cosa copre (sez. 5 F2b):
 * - **default leggibile anche non-admin**: la sezione legge le definizioni del
 *   modello con `fetchSlaDefinitions` e le rende in sola lettura. Il ruolo non
 *   filtra la lettura, governa solo l'override. Nota di contratto: lato Server
 *   la GET `.../models/{id}/definitions` e' **admin di organizzazione**
 *   (`Controller.SLA.pas:136-137`, `SLA_FORBIDDEN`), quindi per un non-admin il
 *   Server risponde 403 e qui si mostra uno stato dedicato
 *   (`sla.start.forbidden`) invece di un elenco vuoto silenzioso: il criterio
 *   non e' soddisfacibile dal solo client ed e' dichiarato come residuo;
 * - **override solo admin**, inviato **nello stesso POST di avvio**: il blocco
 *   `sla-start-override` esiste solo con `isAdmin`; il frammento viene pubblicato
 *   con `onChange` e l'host lo unisce al POST che gia' esiste, una sola
 *   richiesta (`buildSlaStartPayload`). La chiave e' la proposta dichiarata in
 *   `slaStartModel.ts`: il Server non accetta ancora il payload SLA all'avvio;
 * - **preview server con revisioni e risposte fuori ordine**: la stima arriva da
 *   `previewSlaDefinition` con `reference_at` esplicito; le risposte sono
 *   applicate solo se ancora correnti tramite `createSlaPreviewSequencer`, quindi
 *   una risposta vecchia non sovrascrive quella nuova;
 * - **modifiche ai task futuri conservate**: le bozze stanno in stato locale e
 *   nessuna risposta di preview le riscrive. E' la ragione per cui i default sono
 *   in `defaults` e le modifiche in `drafts`: il confronto
 *   (`slaStartDraftIsOverridden`) non tocca la bozza;
 * - **deadline assoluta solo per il processo, con timezone esplicita**: il campo
 *   esiste solo per `SLA_TARGET_PROCESS` (`slaStartAbsoluteDeadlineAllowed`) e il
 *   valore e' un orario di parete nella timezone dell'organizzazione,
 *   convertito con `dayjs.tz(..., timezoneId)`. Se la timezone non e' leggibile
 *   il campo resta disabilitato: nessuna deadline assoluta interpretata con la
 *   timezone del browser.
 *
 * Presentazione (spec `SLA_UI_Impact_Spec.md` sez. 4.1/4.2): la sezione vive
 * nella colonna destra del passo 2, sotto "Dati utente", come **riga
 * riassuntiva**: SLA del processo (default del modello o personalizzato) e
 * scadenza stimata. Per l'admin la riga ("Modifica") apre una **modale** con lo
 * stesso schema della modale "Dati utente" (`FieldsList.tsx`: `Dialog` +
 * `DialogHeader` + Annulla/Conferma): defaults, override (durata relativa o
 * deadline assoluta), motivo, ripristino, stima, suggerimenti e revisione. Le
 * modifiche vivono nello stato di questo componente e alimentano il POST di
 * avvio; **Annulla** (anche Esc/chiusura) riporta ai valori di prima
 * dell'apertura, **Conferma** li tiene ed e' disabilitato finche' la
 * validazione ha errori. Se la configurazione diventa invalida dopo (es.
 * deadline ormai passata) la riga si segna in errore e mostra l'avviso. Per gli
 * altri ruoli la riga porta un lucchetto e non apre nulla. La modale e'
 * `keepMounted`: il DOM dei target resta presente anche a modale chiusa.
 *
 * Residui dichiarati:
 * - il nome leggibile di un target attivita' non e' nel payload delle
 *   definizioni (che non ha `name`): la riga mostra `target_designerid`. Il nome
 *   del nodo richiede il model registry, come per l'editor (sez. 13 punto 2);
 * - `reason` obbligatorio e valore dell'override sono validati localmente; il
 *   422 del Server (`sla_reason_required`, `sla_deadline_in_past`) resta
 *   autoritativo e non e' simulabile finche' l'avvio non accetta il payload SLA;
 * - la sezione vive nel passo 2 del dialog (`step === 1`), quindi tornare al
 *   passo 1 e rientrare **rimonta** la sezione e un override non ancora inviato
 *   viene riletto dai default. Il rimontaggio e' quello del dialog esistente e
 *   non e' stato cambiato: tenere montato il passo 2 nascosto richiederebbe di
 *   verificare il layout dei pannelli ridimensionabili, non eseguibile in questo
 *   ambiente (nessuna app raggiungibile).
 */
interface SlaStartSectionProps {
  idorganization: number;
  idprocessmodel: number;
  /** Nome leggibile del processo: `definitions[]` non lo contiene. */
  processName: string;
  /** Ruolo dell'utente nell'organizzazione selezionata (`organization.administrator`). */
  isAdmin: boolean;
  /** true mentre l'host sta inviando: nessuna modifica durante l'invio. */
  disabled?: boolean;
  /**
   * Frammento da unire al POST di avvio, o `null` quando non c'e' nulla da
   * inviare. Chiamato a ogni cambio dei default/modifiche/motivo/revisione.
   */
  onChange: (payload: SlaStartPayload | null) => void;
}

/** Timezone dell'organizzazione: stessa sorgente che il Server congela nella policy. */
const timezoneRange = () => {
  const today = dayjs().format("YYYY-MM-DD");
  return { from: today, to: today };
};

/**
 * Istante ISO -> orario di parete nell'organizzazione, per il campo
 * `DateTimePicker`. La timezone e' **quella indicata**, mai quella del browser:
 * il picker porta un orario senza fuso e la label dice in quale fuso va
 * letto.
 */
const wallClockFromIso = (iso: string, timezoneId: string): string =>
  dayjs.utc(iso).tz(timezoneId).format("YYYY-MM-DDTHH:mm");

/** Orario di parete nell'organizzazione -> istante ISO UTC. */
export const isoFromWallClock = (
  wallClock: string,
  timezoneId: string,
): string | null => {
  if (!wallClock) return null;
  const parsed = dayjs.tz(wallClock, timezoneId);
  return parsed.isValid() ? parsed.toISOString() : null;
};

const SlaStartSection = ({
  idorganization,
  idprocessmodel,
  processName,
  isAdmin,
  disabled = false,
  onChange,
}: SlaStartSectionProps) => {
  const translations = useTranslations();
  const user = useUser();

  /** Default letti dal modello: non modificati dalle bozze (task futuri conservati). */
  const [defaults, setDefaults] = useState<SlaStartDraft[] | null>(null);
  const [drafts, setDrafts] = useState<SlaStartDraft[]>([]);
  const [revision, setRevision] = useState<number | null>(null);
  const [reason, setReason] = useState<string>("");
  const [timezoneId, setTimezoneId] = useState<string | null>(null);
  const [timezoneUnavailable, setTimezoneUnavailable] = useState<boolean>(false);
  // Si parte in caricamento: senza questo, il primo render mostrerebbe lo stato
  // "nessuno SLA" (elenco vuoto) prima che la GET sia partita.
  const [loading, setLoading] = useState<boolean>(true);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);

  const [previewKey, setPreviewKey] = useState<string>("");
  const [preview, setPreview] = useState<SlaPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  /**
   * Sorgente della proposta non raggiungibile (F2c): e' uno stato dichiarato
   * della sezione, **non** un errore del flusso. L'avvio resta possibile perche'
   * `buildSlaStartPayload` non legge nulla di questa sezione.
   */
  const [previewFailed, setPreviewFailed] = useState<boolean>(false);
  /** Modale SLA aperta dall'admin (sez. 4.2). */
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  /** Valori prima dell'apertura: Annulla li ripristina (come "Dati utente"). */
  const modalSnapshotRef = useRef<{
    drafts: SlaStartDraft[];
    reason: string;
    previewKey: string;
    absoluteMode: boolean;
  } | null>(null);
  /**
   * Tipo di scadenza del processo scelto dall'admin: durata relativa (valore +
   * unita') o deadline assoluta (data/ora nella timezone dell'organizzazione).
   */
  const [absoluteMode, setAbsoluteMode] = useState<boolean>(false);
  /** Nomi dei nodi dal BPMN del modello: le definizioni hanno solo il designerid. */
  const [nodeNames, setNodeNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { data } = await processmodelsApi.get<{ model?: string }>(`/${idprocessmodel}`);
        if (isMounted) setNodeNames(data?.model ? slaNodeNamesFromBpmnXml(data.model) : {});
      } catch {
        // Nomi non leggibili: si mostra l'identificativo del nodo.
        if (isMounted) setNodeNames({});
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [idprocessmodel]);

  const sequencerRef = useRef(createSlaPreviewSequencer(0));
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const unitLabel = useCallback(
    (unit: SlaDurationUnit | null) =>
      unit === null
        ? ""
        : getTranslation(translations, SLA_DURATION_UNIT_LABEL_KEYS[unit]),
    [translations],
  );

  /** Formato data/ora nella lingua app **e** nella timezone dell'organizzazione. */
  const dateTimeLabel = useCallback(
    (iso: string): string => {
      if (!iso) return "";
      const zone = timezoneId || dayjs.tz.guess();
      try {
        return new Intl.DateTimeFormat(user?.i18n || navigator.language, {
          dateStyle: "short",
          timeStyle: "short",
          timeZone: zone,
        }).format(new Date(iso));
      } catch {
        return dayjs.utc(iso).tz(zone).format("YYYY-MM-DD HH:mm");
      }
    },
    [timezoneId, user?.i18n],
  );

  // ── Default del modello + timezone dell'organizzazione ─────────────────────

  const loadDefaults = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    setLoadError(false);
    // La stima precedente e' di un'altra revisione: si scarta insieme al
    // sequencer, che invalida le risposte ancora in volo.
    setPreview(null);
    setPreviewFailed(false);
    try {
      const response = await fetchSlaDefinitions(idorganization, idprocessmodel);
      const fromModel = response.definitions.map((dto) =>
        slaStartDraftFromDefinition(
          dto,
          dto.targettype === SLA_TARGET_PROCESS
            ? processName
            : dto.targetdesignerid ?? "",
        ),
      );
      // Il processo puo' avere uno SLA anche senza definizione nel modello:
      // per l'admin il target di processo c'e' sempre (vuoto, `hasModelSla`
      // false), cosi' l'override relativo/assoluto e' sempre offerto.
      const nextDefaults =
        isAdmin && !fromModel.some((draft) => draft.targetType === SLA_TARGET_PROCESS)
          ? [
              emptySlaStartDraft({
                targetType: SLA_TARGET_PROCESS,
                targetDesignerId: null,
                name: processName,
              }),
              ...fromModel,
            ]
          : fromModel;
      setAbsoluteMode(false);
      setDefaults(nextDefaults);
      setDrafts(nextDefaults);
      const nextRevision = slaRevisionFromMeta(response.meta);
      setRevision(nextRevision);
      sequencerRef.current.setRevision(nextRevision ?? 0);
      setPreviewKey(
        nextDefaults.some((draft) => draft.targetType === SLA_TARGET_PROCESS)
          ? PROCESS_TARGET_KEY
          : nextDefaults.length > 0
            ? slaStartTargetKey(nextDefaults[0])
            : "",
      );
    } catch (error) {
      const apiError = toSlaApiError(error, SLA_ERROR_CODES.modelNotFound);
      // 403: la rotta delle definizioni e' admin di organizzazione lato Server.
      // Non e' un errore di caricamento, e' un permesso: si dice in chiaro.
      if (apiError.status === 403) setForbidden(true);
      else setLoadError(true);
      setDefaults(NO_DRAFTS);
      setDrafts(NO_DRAFTS);
    } finally {
      setLoading(false);
    }
  }, [idorganization, idprocessmodel, processName, isAdmin]);

  useEffect(() => {
    let isMounted = true;
    const range = timezoneRange();
    (async () => {
      try {
        const calendar = await getOrganizationCalendar(
          idorganization,
          range.from,
          range.to,
        );
        if (isMounted && calendar?.timezoneId) setTimezoneId(calendar.timezoneId);
        else if (isMounted) setTimezoneUnavailable(true);
      } catch {
        // La timezone non e' deducibile: la deadline assoluta resta disabilitata
        // (nessun orario interpretato con il fuso del browser).
        if (isMounted) setTimezoneUnavailable(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [idorganization]);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  // ── Override (admin-only) ─────────────────────────────────────────────────

  const updateDraft = useCallback(
    (key: string, patch: Partial<SlaStartDraft>) => {
      setDrafts((current) =>
        current.map((draft) =>
          slaStartTargetKey(draft) === key ? { ...draft, ...patch } : draft,
        ),
      );
    },
    [],
  );

  const resetOverrides = useCallback(() => {
    setDrafts(defaults ?? NO_DRAFTS);
    setReason("");
    setAbsoluteMode(false);
  }, [defaults]);

  /**
   * Cambio del tipo di scadenza del processo. Relativa: la deadline assoluta si
   * azzera. Assoluta: la durata torna al default del modello, cosi' non resta
   * un secondo override nascosto accanto alla deadline.
   */
  const changeDeadlineMode = useCallback(
    (key: string, absolute: boolean) => {
      setAbsoluteMode(absolute);
      if (absolute) {
        const base = (defaults ?? NO_DRAFTS).find(
          (draft) => slaStartTargetKey(draft) === key,
        );
        updateDraft(key, {
          durationValue: base?.durationValue ?? null,
          durationUnit: base?.durationUnit ?? null,
        });
      } else {
        updateDraft(key, { absoluteDeadlineAt: null });
      }
    },
    [defaults, updateDraft],
  );

  const validation = useMemo(
    () =>
      validateSlaStartOverride(
        defaults ?? NO_DRAFTS,
        drafts,
        reason,
        new Date().toISOString(),
      ),
    [defaults, drafts, reason],
  );

  // ── Pubblicazione del frammento per il POST di avvio ──────────────────────

  useEffect(() => {
    if (revision === null) {
      onChange(null);
      return;
    }
    const payload = buildSlaStartPayload(
      defaults ?? NO_DRAFTS,
      drafts,
      revision,
      reason,
    );
    onChange(payload.targets.length > 0 ? payload : null);
  }, [defaults, drafts, reason, revision, onChange]);

  // ── Preview (server, con guardia sulle risposte fuori ordine) ─────────────

  const previewedDraft = useMemo(
    () => drafts.find((draft) => slaStartTargetKey(draft) === previewKey) ?? null,
    [drafts, previewKey],
  );

  /**
   * Proposte della superficie (F2c). Sorgente: la stima del Server, adattata da
   * `slaSuggestionFromPreview`. Nessun'altra sorgente esiste oggi — non c'e'
   * alcuna API di proposte AI nel client ne' lato Server — e il contratto che
   * servirebbe e' dichiarato in `model/slaSuggestionModel.ts`, non inventato qui.
   */
  const suggestions = useMemo<SlaSuggestion[]>(
    () =>
      preview && previewKey
        ? [slaSuggestionFromPreview(previewKey, preview, revision)]
        : [],
    [preview, previewKey, revision],
  );

  /**
   * Applicazione **esplicita**: e' l'unico punto in cui una proposta entra nella
   * bozza, e si raggiunge solo dal comando di conferma della sezione (F2c: mai
   * applicata silenziosamente). `applySlaSuggestion` e' pura e non invia nulla:
   * il valore entra nel campo editabile dell'override e li' resta modificabile.
   * Una proposta non applicabile restituisce la stessa bozza: nessuna scrittura.
   */
  const applySuggestion = useCallback(
    (suggestion: SlaSuggestion) => {
      const current = drafts.find(
        (draft) => slaStartTargetKey(draft) === suggestion.targetKey,
      );
      if (!current) return;
      const next = applySlaSuggestion(current, suggestion, revision);
      if (next === current) return;
      updateDraft(suggestion.targetKey, {
        absoluteDeadlineAt: next.absoluteDeadlineAt,
      });
    },
    [drafts, revision, updateDraft],
  );

  const runPreview = useCallback(async () => {
    if (!previewedDraft || !isSlaStartDraftConfigured(previewedDraft)) {
      // Nessuna durata (es. processo senza SLA nel modello): niente da stimare.
      setPreview(null);
      setPreviewFailed(false);
      return;
    }
    const token = sequencerRef.current.begin();
    setPreviewLoading(true);
    try {
      const response = await previewSlaDefinition(
        idorganization,
        slaStartPreviewRequest(previewedDraft, new Date().toISOString()),
      );
      // La risposta si applica solo se e' ancora quella corrente: una stima di
      // una revisione precedente, arrivata dopo, viene scartata.
      sequencerRef.current.settle(token, setPreview, response);
      sequencerRef.current.settle(token, setPreviewFailed, false);
    } catch {
      // Sorgente non raggiungibile: si dichiara e non blocca (F2c). La bozza e
      // il frammento di avvio non dipendono dalla stima.
      sequencerRef.current.settle(token, setPreview, null);
      sequencerRef.current.settle(token, setPreviewFailed, true);
    } finally {
      if (sequencerRef.current.isCurrent(token)) setPreviewLoading(false);
    }
  }, [idorganization, previewedDraft]);

  useEffect(() => {
    if (!previewKey) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runPreview();
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    previewKey,
    previewedDraft?.durationValue,
    previewedDraft?.durationUnit,
    previewedDraft?.riskRemainingPercent,
    previewedDraft?.absoluteDeadlineAt,
  ]);

  // ── Resa ──────────────────────────────────────────────────────────────────

  const targetLabel = (draft: SlaStartDraft): string => {
    const kind =
      draft.targetType === SLA_TARGET_PROCESS
        ? getTranslation(translations, SLA_EDITOR_TEXT_KEYS.targetProcess)
        : getTranslation(translations, SLA_EDITOR_TEXT_KEYS.targetActivity);
    const name =
      draft.targetType === SLA_TARGET_PROCESS
        ? draft.name
        : (draft.targetDesignerId && nodeNames[draft.targetDesignerId]) || draft.name;
    return name ? `${kind}: ${name}` : kind;
  };

  // ── Riga riassuntiva (sezione chiusa) ──────────────────────────────────────

  const processDraft =
    drafts.find((draft) => slaStartTargetKey(draft) === PROCESS_TARGET_KEY) ?? null;
  const processOverridden =
    processDraft !== null && slaStartDraftIsOverridden(processDraft, defaults ?? []);
  const anyOverridden = drafts.some((draft) =>
    slaStartDraftIsOverridden(draft, defaults ?? []),
  );
  const summaryValue = (() => {
    if (!processDraft) return getTranslation(translations, SLA_START_TEXT_KEYS.targetNoSla);
    if (processDraft.absoluteDeadlineAt) {
      return `${getTranslation(translations, SLA_START_TEXT_KEYS.overrideDeadline)}: ${dateTimeLabel(
        processDraft.absoluteDeadlineAt,
      )}`;
    }
    if (isSlaStartDraftConfigured(processDraft)) {
      return `${processDraft.durationValue} ${unitLabel(processDraft.durationUnit)}`;
    }
    return getTranslation(translations, SLA_START_TEXT_KEYS.targetNoSla);
  })();
  /**
   * Scadenza personalizzata (modalita' assoluta, solo processo): la preview del
   * Server e' calcolata dalla durata relativa di default (`slaStartPreviewRequest`
   * non invia `absoluteDeadlineAt`), quindi "ora + durata" non va mostrata ne'
   * proposta: sostituirebbe la data scelta dall'admin.
   */
  const customDeadlineShown = absoluteMode && previewKey === PROCESS_TARGET_KEY;
  const summaryEstimate =
    !absoluteMode &&
    preview &&
    preview.estimable &&
    previewKey === PROCESS_TARGET_KEY
      ? dateTimeLabel(preview.deadline_at)
      : null;
  const summaryInvalid = validation.errors.length > 0;
  const openModal = () => {
    if (!isAdmin || disabled) return;
    modalSnapshotRef.current = { drafts, reason, previewKey, absoluteMode };
    setModalOpen(true);
  };
  /** Annulla, Esc o chiusura: si torna ai valori di prima dell'apertura. */
  const cancelModal = () => {
    const snapshot = modalSnapshotRef.current;
    if (snapshot) {
      setDrafts(snapshot.drafts);
      setReason(snapshot.reason);
      setPreviewKey(snapshot.previewKey);
      setAbsoluteMode(snapshot.absoluteMode);
    }
    modalSnapshotRef.current = null;
    setModalOpen(false);
  };
  const confirmModal = () => {
    if (summaryInvalid) return;
    modalSnapshotRef.current = null;
    setModalOpen(false);
  };

  return (
    <Box
      id="sla-start-section"
      data-testid="sla-start-section"
      data-sla-admin={isAdmin ? "true" : "false"}
      data-sla-forbidden={forbidden ? "true" : "false"}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontSize: 14,
          fontWeight: 800,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          mb: 1.5,
        }}
      >
        {getTranslation(translations, SLA_START_TEXT_KEYS.title)}
      </Typography>

      {isSlaFixtureMode() && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.fixtureNotice)}
        </Alert>
      )}

      {loading && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          data-testid="sla-start-loading"
        >
          <CircularProgress size={16} />
          <Typography variant="body2">
            {getTranslation(translations, SLA_START_TEXT_KEYS.loading)}
          </Typography>
        </Stack>
      )}

      {!loading && forbidden && (
        <Alert severity="info" data-testid="sla-start-forbidden" sx={{ mb: 1.5 }}>
          {getTranslation(translations, SLA_START_TEXT_KEYS.forbidden)}
        </Alert>
      )}

      {!loading && !forbidden && loadError && (
        <Alert severity="error" data-testid="sla-start-error" sx={{ mb: 1.5 }}>
          {getTranslation(translations, SLA_START_TEXT_KEYS.loadError)}
        </Alert>
      )}

      {!loading && !forbidden && !loadError && (
        <Stack spacing={1.5}>
          {/* Riga riassuntiva (sez. 4.1): cliccabile solo per l'admin. */}
          <Box
            id="sla-start-summary"
            data-testid="sla-start-summary"
            data-sla-overridden={anyOverridden ? "true" : "false"}
            data-sla-invalid={summaryInvalid ? "true" : "false"}
            role={isAdmin ? "button" : undefined}
            tabIndex={isAdmin && !disabled ? 0 : undefined}
            aria-haspopup={isAdmin ? "dialog" : undefined}
            aria-invalid={summaryInvalid || undefined}
            onClick={openModal}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openModal();
              }
            }}
            sx={{
              p: 1.25,
              border: 1,
              borderColor: summaryInvalid ? "error.main" : "divider",
              borderRadius: 2,
              cursor: isAdmin && !disabled ? "pointer" : "default",
              "&:hover": isAdmin && !disabled ? { bgcolor: "action.hover" } : undefined,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {summaryInvalid ? (
                <ErrorOutlineIcon fontSize="small" color="error" />
              ) : (
                <TimerOutlinedIcon fontSize="small" color="action" />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  data-testid="sla-start-summary-value"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {summaryValue}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="sla-start-summary-source"
                  sx={{ display: "block" }}
                >
                  {getTranslation(
                    translations,
                    anyOverridden || processOverridden
                      ? SLA_START_TEXT_KEYS.summaryCustom
                      : SLA_START_TEXT_KEYS.summaryDefault,
                  )}
                  {summaryEstimate && (
                    <>
                      {" · "}
                      {getTranslation(translations, SLA_START_TEXT_KEYS.previewDeadline)}
                      {": "}
                      {summaryEstimate}
                    </>
                  )}
                </Typography>
                {summaryInvalid && (
                  <Typography
                    variant="caption"
                    color="error"
                    data-testid="sla-start-summary-invalid"
                    sx={{ display: "block" }}
                  >
                    {getTranslation(translations, SLA_START_TEXT_KEYS.summaryInvalid)}
                  </Typography>
                )}
              </Box>
              {isAdmin ? (
                <Stack
                  direction="row"
                  spacing={0.25}
                  alignItems="center"
                  sx={{ color: "primary.main", flexShrink: 0 }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {getTranslation(translations, SLA_START_TEXT_KEYS.summaryEdit)}
                  </Typography>
                  <ChevronRightIcon fontSize="small" />
                </Stack>
              ) : (
                <Tooltip
                  title={getTranslation(translations, SLA_START_TEXT_KEYS.summaryLocked)}
                  placement="top"
                  arrow
                >
                  <LockOutlinedIcon
                    fontSize="small"
                    color="action"
                    tabIndex={0}
                    aria-label={getTranslation(
                      translations,
                      SLA_START_TEXT_KEYS.summaryLocked,
                    )}
                    data-testid="sla-start-summary-locked"
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>

          {validation.errors.includes(SLA_START_TEXT_KEYS.deadlineInPast) && (
            <Alert severity="error" data-testid="sla-start-deadline-in-past">
              {getTranslation(translations, SLA_START_TEXT_KEYS.deadlineInPast)}
            </Alert>
          )}

          {isAdmin && (
          <Dialog
            open={modalOpen}
            onClose={cancelModal}
            keepMounted
            fullWidth
            maxWidth="md"
            aria-labelledby="sla-start-modal-title"
            data-testid="sla-start-modal"
          >
          <DialogHeader id="sla-start-modal-title" onClose={cancelModal}>
            {getTranslation(translations, SLA_START_TEXT_KEYS.modalTitle)}
          </DialogHeader>
          <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }} id="sla-start-details" data-testid="sla-start-details">
          {/* Due colonne da md in su: default compatti a sinistra, override a
              destra; impilati sugli schermi piccoli. */}
          <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={5}>
          <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.secondary">
              {getTranslation(translations, SLA_START_TEXT_KEYS.defaultsTitle)}
            </Typography>
            {/* Timezone dell'organizzazione: dichiarata una volta per la
                superficie. Le date assolute e gli orari di parete sono in
                questo fuso, indipendentemente dalla lingua e dal browser. */}
            {timezoneId && (
              <Chip
                size="small"
                variant="outlined"
                data-testid="sla-start-timezone"
                label={`${getTranslation(translations, SLA_START_TEXT_KEYS.timezone)}: ${timezoneId}`}
                sx={{ height: 22, fontSize: "0.7rem", maxWidth: "60%" }}
              />
            )}
          </Stack>

          <Stack
            spacing={0}
            data-testid="sla-start-default-list"
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 260,
              overflowY: "auto",
              "& > *:not(:last-child)": { borderBottom: 1, borderColor: "divider" },
            }}
          >
            {drafts.length === 0 && (
              <Alert severity="info" data-testid="sla-start-empty">
                {getTranslation(translations, SLA_START_TEXT_KEYS.targetNoSla)}
              </Alert>
            )}
            {drafts.map((draft) => {
              const key = slaStartTargetKey(draft);
              const overridden = slaStartDraftIsOverridden(draft, defaults ?? []);
              return (
                <Stack
                  key={key}
                  id={`sla-start-target-${key}`}
                  data-testid={`sla-start-target-${key}`}
                  data-sla-overridden={overridden ? "true" : "false"}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ px: 1, py: 0.5, minHeight: 32 }}
                >
                  <Typography
                    variant="body2"
                    title={targetLabel(draft)}
                    sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {targetLabel(draft)}
                  </Typography>
                  {isSlaStartDraftConfigured(draft) ? (
                    <Chip
                      size="small"
                      color={overridden ? "primary" : "default"}
                      variant={overridden ? "filled" : "outlined"}
                      data-testid={`sla-start-default-duration-${key}`}
                      sx={{ height: 22, fontSize: "0.72rem", flexShrink: 0 }}
                      label={
                        <>
                          {draft.durationValue} {unitLabel(draft.durationUnit)}
                          {slaStartReadsAbsoluteFormat(draft) && (
                            <span data-testid={`sla-start-absolute-format-${key}`}>
                              {" "}
                              {getTranslation(
                                translations,
                                SLA_START_TEXT_KEYS.overrideDeadline,
                              )}
                            </span>
                          )}
                        </>
                      }
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {getTranslation(
                        translations,
                        SLA_START_TEXT_KEYS.targetNoSla,
                      )}
                    </Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>

          </Stack>
          </Grid>
          <Grid item xs={12} md={7}>
          {/* Override: solo admin (sez. 5 F2b). Per gli altri ruoli il blocco
              non esiste affatto, non e' disabilitato: nessuna informazione e'
              nascosta solo perche' inerte. */}
          {isAdmin && (
            <Box
              id="sla-start-override"
              data-testid="sla-start-override"
              sx={{
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {getTranslation(translations, SLA_START_TEXT_KEYS.overrideTitle)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getTranslation(translations, SLA_START_TEXT_KEYS.overrideHint)}
              </Typography>

              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                {drafts.map((draft) => {
                  const key = slaStartTargetKey(draft);
                  const deadlineAllowedForTarget = slaStartAbsoluteDeadlineAllowed(
                    draft.targetType,
                  );
                  return (
                    <Stack key={key} spacing={1}>
                      <Typography variant="caption" fontWeight={600}>
                        {targetLabel(draft)}
                      </Typography>
                      {/* Processo: scelta esplicita fra durata relativa e
                          deadline assoluta. L'assoluta richiede la timezone
                          dell'organizzazione (nessun fuso del browser). */}
                      {deadlineAllowedForTarget && (
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          color="primary"
                          fullWidth
                          disabled={disabled}
                          value={absoluteMode ? "absolute" : "relative"}
                          aria-label={getTranslation(
                            translations,
                            SLA_START_TEXT_KEYS.overrideMode,
                          )}
                          data-testid="sla-start-override-mode"
                          onChange={(_, value: "relative" | "absolute" | null) => {
                            if (value) changeDeadlineMode(key, value === "absolute");
                          }}
                        >
                          <ToggleButton
                            value="relative"
                            id="sla-start-override-mode-relative"
                            sx={{ textTransform: "none" }}
                          >
                            {getTranslation(
                              translations,
                              SLA_START_TEXT_KEYS.overrideModeRelative,
                            )}
                          </ToggleButton>
                          <ToggleButton
                            value="absolute"
                            id="sla-start-override-mode-absolute"
                            disabled={disabled || !timezoneId}
                            sx={{ textTransform: "none" }}
                          >
                            {getTranslation(
                              translations,
                              SLA_START_TEXT_KEYS.overrideModeAbsolute,
                            )}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}
                      {!(deadlineAllowedForTarget && absoluteMode) && (
                      <Stack direction="row" spacing={1}>
                        <TextField
                          id={`sla-start-override-duration-${key}`}
                          size="small"
                          type="number"
                          fullWidth
                          disabled={disabled}
                          label={getTranslation(
                            translations,
                            SLA_START_TEXT_KEYS.overrideDuration,
                          )}
                          inputProps={{ min: 0, step: "any" }}
                          value={draft.durationValue ?? ""}
                          onChange={(event) =>
                            updateDraft(key, {
                              durationValue:
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                            })
                          }
                        />
                        <TextField
                          id={`sla-start-override-unit-${key}`}
                          select
                          size="small"
                          fullWidth
                          disabled={disabled}
                          label={getTranslation(
                            translations,
                            SLA_EDITOR_TEXT_KEYS.durationUnit,
                          )}
                          value={draft.durationUnit ?? ""}
                          onChange={(event) =>
                            updateDraft(key, {
                              durationUnit:
                                event.target.value === ""
                                  ? null
                                  : (Number(event.target.value) as SlaDurationUnit),
                            })
                          }
                        >
                          {SLA_DURATION_UNITS.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unitLabel(unit)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                      )}
                      {deadlineAllowedForTarget ? (
                        absoluteMode && (
                        <>
                          <DateTimePicker
                            ampm={false}
                            disabled={disabled || !timezoneId}
                            label={getTranslation(
                              translations,
                              SLA_START_TEXT_KEYS.overrideDeadline,
                            )}
                            slotProps={{
                              textField: {
                                id: `sla-start-override-deadline-${key}`,
                                size: "small",
                                fullWidth: true,
                                InputLabelProps: { shrink: true },
                                ...({
                                  "data-testid": "sla-start-override-deadline",
                                } as Record<string, string>),
                              },
                            }}
                            value={
                              draft.absoluteDeadlineAt && timezoneId
                                ? dayjs(
                                    wallClockFromIso(
                                      draft.absoluteDeadlineAt,
                                      timezoneId,
                                    ),
                                  )
                                : null
                            }
                            onChange={(value: dayjs.Dayjs | null) =>
                              updateDraft(key, {
                                absoluteDeadlineAt:
                                  timezoneId && value && value.isValid()
                                    ? isoFromWallClock(
                                        value.format("YYYY-MM-DDTHH:mm"),
                                        timezoneId,
                                      )
                                    : null,
                              })
                            }
                          />
                          <Typography
                            variant="caption"
                            data-testid="sla-start-override-timezone"
                            sx={{ display: "block" }}
                          >
                            {getTranslation(
                              translations,
                              SLA_START_TEXT_KEYS.timezone,
                            )}
                            {": "}
                            {timezoneId ?? ""}
                          </Typography>
                        </>
                        )
                      ) : (
                        <Typography
                          variant="caption"
                          data-testid="sla-start-override-deadline-blocked"
                          color="text.secondary"
                        >
                          {getTranslation(
                            translations,
                            SLA_START_TEXT_KEYS.overrideDeadlineProcessOnly,
                          )}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}

                <TextField
                  id="sla-start-override-reason"
                  data-testid="sla-start-override-reason"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={disabled}
                  required
                  error={validation.errors.includes(
                    SLA_START_TEXT_KEYS.reasonRequired,
                  )}
                  helperText={
                    validation.errors.includes(
                      SLA_START_TEXT_KEYS.reasonRequired,
                    )
                      ? getTranslation(
                          translations,
                          SLA_START_TEXT_KEYS.reasonRequired,
                        )
                      : undefined
                  }
                  label={getTranslation(
                    translations,
                    SLA_START_TEXT_KEYS.overrideReason,
                  )}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />

                <Stack direction="row" spacing={1} alignItems="center">
                  <FlowentiButton
                    id="sla-start-override-reset"
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    onClick={resetOverrides}
                  >
                    {getTranslation(
                      translations,
                      SLA_START_TEXT_KEYS.overrideReset,
                    )}
                  </FlowentiButton>
                </Stack>
              </Stack>
            </Box>
          )}
          </Grid>
          </Grid>

          {timezoneUnavailable && (
            <Alert severity="warning" data-testid="sla-start-timezone-unavailable">
              {getTranslation(translations, SLA_START_TEXT_KEYS.timezoneUnavailable)}
            </Alert>
          )}

          {/* Preview server della bozza selezionata. */}
          {drafts.length > 0 && (
            <Box data-testid="sla-start-preview">
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  id="sla-start-preview-target"
                  select
                  size="small"
                  fullWidth
                  label={getTranslation(
                    translations,
                    SLA_START_TEXT_KEYS.previewTitle,
                  )}
                  value={previewKey}
                  onChange={(event) => setPreviewKey(event.target.value)}
                >
                  {drafts.map((draft) => (
                    <MenuItem
                      key={slaStartTargetKey(draft)}
                      value={slaStartTargetKey(draft)}
                    >
                      {targetLabel(draft)}
                    </MenuItem>
                  ))}
                </TextField>
                <FlowentiButton
                  id="sla-start-preview-refresh"
                  variant="outlined"
                  size="small"
                  disabled={previewLoading || !previewedDraft}
                  onClick={() => runPreview()}
                >
                  {getTranslation(
                    translations,
                    SLA_START_TEXT_KEYS.previewRefresh,
                  )}
                </FlowentiButton>
              </Stack>

              {previewLoading && (
                <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                  <CircularProgress size={14} />
                  <Typography variant="body2">
                    {getTranslation(translations, SLA_START_TEXT_KEYS.loading)}
                  </Typography>
                </Stack>
              )}

              {customDeadlineShown && processDraft?.absoluteDeadlineAt && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    data-testid="sla-start-preview-deadline"
                    data-sla-deadline-source="custom"
                  >
                    {getTranslation(
                      translations,
                      SLA_START_TEXT_KEYS.overrideDeadline,
                    )}
                    {": "}
                    {dateTimeLabel(processDraft.absoluteDeadlineAt)}
                  </Typography>
                </Stack>
              )}

              {!customDeadlineShown && preview && !preview.estimable && (
                <Alert
                  severity="warning"
                  data-testid="sla-start-preview-unavailable"
                  sx={{ mt: 1 }}
                >
                  {getTranslation(
                    translations,
                    SLA_START_TEXT_KEYS.previewUnavailable,
                  )}
                </Alert>
              )}

              {!customDeadlineShown && preview && preview.estimable && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    data-testid="sla-start-preview-deadline"
                  >
                    {getTranslation(
                      translations,
                      SLA_START_TEXT_KEYS.previewDeadline,
                    )}
                    {": "}
                    {dateTimeLabel(preview.deadline_at)}
                  </Typography>
                  <Typography
                    variant="body2"
                    data-testid="sla-start-preview-risk"
                  >
                    {getTranslation(
                      translations,
                      SLA_START_TEXT_KEYS.previewRisk,
                    )}
                    {": "}
                    {dateTimeLabel(preview.risk_at)}
                  </Typography>
                  <Typography
                    variant="caption"
                    data-testid="sla-start-preview-as-of"
                  >
                    {getTranslation(
                      translations,
                      SLA_START_TEXT_KEYS.previewAsOf,
                    )}
                    {": "}
                    {dateTimeLabel(preview.as_of)}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}

          {/* Proposte (F2c). La stima e' gia' resa dal blocco sopra
              (`sla-start-preview-deadline`/`-risk`/`-as-of`/`-unavailable`),
              quindi qui c'e' il solo gesto di **conferma esplicita**: senza di
              esso la proposta non entra nella bozza. Sul target attivita' la
              deadline assoluta non e' rappresentabile (sez. 5 F2b): nessun
              comando inerte, la proposta resta informativa e il criterio e'
              dichiarato come residuo, non simulato. */}
          {/* In modalita' assoluta nessuna proposta: la stima nasce dalla durata
              di default e "Applica" sovrascriverebbe la scadenza scelta. */}
          {/* Hidden for now: nessuna proposta mostrata ne' applicabile. */}
          {SLA_START_AI_SUGGESTIONS_VISIBLE && !absoluteMode && (
          <SlaSuggestions
            testIdPrefix="sla-start-suggestions"
            suggestions={suggestions}
            modelRevision={revision}
            estimateRenderedByCaller
            sourceError={previewFailed}
            loading={previewLoading}
            onApply={
              isAdmin &&
              previewedDraft !== null &&
              slaSuggestionApplySupported(previewedDraft.targetType)
                ? applySuggestion
                : undefined
            }
            isApplied={(suggestion) =>
              previewedDraft !== null &&
              isSlaSuggestionApplied(previewedDraft, suggestion)
            }
            formatDateTime={dateTimeLabel}
          />
          )}

          {revision !== null && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="sla-start-revision"
            >
              {getTranslation(translations, SLA_START_TEXT_KEYS.previewRevision)}
              {": "}
              {revision}
            </Typography>
          )}
          {validation.errors.includes(SLA_START_TEXT_KEYS.deadlineInPast) && (
            <Alert severity="error" data-testid="sla-start-modal-deadline-in-past">
              {getTranslation(translations, SLA_START_TEXT_KEYS.deadlineInPast)}
            </Alert>
          )}
          </Stack>
          </DialogContent>
          <DialogActions>
            <FlowentiButton
              id="sla-start-modal-cancel"
              size="small"
              variant="outlined"
              onClick={cancelModal}
            >
              {getTranslation(translations, SLA_START_TEXT_KEYS.modalCancel)}
            </FlowentiButton>
            <FlowentiButton
              id="sla-start-modal-confirm"
              variant="contained"
              size="small"
              disabled={summaryInvalid || disabled}
              onClick={confirmModal}
            >
              {getTranslation(translations, SLA_START_TEXT_KEYS.modalConfirm)}
            </FlowentiButton>
          </DialogActions>
          </Dialog>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default SlaStartSection;
